<template>
  <div class="pc">
    <FilterBar v-if="visibleFilters.length" :filters="visibleFilters" :values="values" @change="setFilter" />
    <div v-if="spec.template" class="pc-stagebg" :style="stageStyle(spec)">
      <PageTemplate
        :template="spec.template"
        :placements="spec.placements || []"
        :widgets="widgets"
        :host-props="{ pageId, sessionId, subject, filters: values, preloaded, spec, host }"
        @drill-entity="$emit('drill-entity', $event)"
        @select-patient="$emit('select-patient', $event)"
      />
    </div>
  </div>
</template>

<script>
import FilterBar from './FilterBar.vue';
import PageTemplate from './PageTemplate.vue';
import { filterDefaults, stageStyle } from './pageLayout';

export default {
  name: 'PageCanvas',
  components: { FilterBar, PageTemplate },
  provide() {
    return { pageRows: this.cachedRows, forgetSources: this.forgetSources, joinBatch: this.joinBatch };
  },
  props: {
    pageId: { type: Number, default: null },
    sessionId: { type: String, default: '' },
    subject: { type: Object, default: null },
    spec: { type: Object, required: true },
    widgets: { type: Object, default: () => ({}) },
    preloaded: { type: Object, default: null },
    host: { type: Object, default: () => ({}) },
    hideEntityFilter: { type: Boolean, default: false },
  },
  emits: ['drill-entity', 'select-patient'],
  data() {
    return { values: filterDefaults(this.spec) };
  },
  computed: {
    visibleFilters() {
      return (this.spec.filters || []).filter((f) => !(this.hideEntityFilter && f.type === 'entities'));
    },
  },
  watch: {
    spec(next) {
      this.values = filterDefaults(next);
      this.rowsCache.clear();
    },
    values: { deep: true, handler() { this.rowsCache.clear(); } },
    host: { deep: true, handler() { this.rowsCache.clear(); } },
  },
  created() {
    this.rowsCache = new Map();
  },
  methods: {
    stageStyle,
    setFilter(name, value) {
      this.values = { ...this.values, [name]: value };
    },
    cachedRows(key, load) {
      if (!this.rowsCache.has(key)) this.rowsCache.set(key, load());
      return this.rowsCache.get(key);
    },
    joinBatch(promise) {
      if (!this.batch) {
        const batch = { list: [] };
        batch.done = new Promise((resolve) => {
          setTimeout(async () => {
            let seen;
            do {
              seen = batch.list.length;
              await Promise.allSettled(batch.list);
            } while (seen !== batch.list.length);
            if (this.batch === batch) this.batch = null;
            resolve();
          }, 0);
        });
        this.batch = batch;
      }
      this.batch.list.push(promise);
      return this.batch.done.then(() => promise);
    },
    async exportRows() {
      const sheets = [];
      for (const [key, pending] of this.rowsCache.entries()) {
        let loaded;
        try { loaded = await pending; } catch { continue; }
        const list = Array.isArray(loaded) ? loaded : (loaded && Array.isArray(loaded.rows) ? loaded.rows : null);
        if (!list || !list.length || typeof list[0] !== 'object') continue;
        sheets.push({ name: String(key).split('|')[0], rows: list });
      }
      return sheets;
    },
    forgetSources(names, scope) {
      names.forEach((name) => this.rowsCache.delete(`${name}|${JSON.stringify(scope)}`));
    },
  },
};
</script>

<style scoped>
.pc { display: flex; flex-direction: column; gap: 10px; }
</style>
